import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import SmartFormImportManagerStore from '~/client/src/shared/stores/domain/SmartFormImportManager.store'
import { BULLET } from '~/client/src/shared/utils/usefulStrings'

import '~/client/src/desktop/views/ProjectSetUp/components/PermitsSetUp/components/FormTypesList/components/SmartFormImport/SmartFormImportProgressDialog/SmartFormImportProgressDialog.scss'

const statusMessages = [
  Localization.translator.detectingHeaders,
  Localization.translator.findingFields,
  Localization.translator.scanningCheckboxes,
  Localization.translator.organizingWorkflowSteps,
  Localization.translator.aligningWithStruxHubSchema,
  Localization.translator.doubleCheckingFieldTypes,
  Localization.translator.tidyingUpYourForm,
  Localization.translator.sharpeningDigitalPencils,
  Localization.translator.sortingThingsIntoTheRightBins,
]

const tipMessages = [
  Localization.translator.moveFieldsIntoDifferentWorkflowStepsAfterImport,
  Localization.translator.addWorkflowStepsForApprovalsInspectionsAndStartTimes,
  Localization.translator
    .routeWorkflowStepsToBallInCourtUsersWithConditionalLogic,
  Localization.translator.notifyWorkflowMembersAfterSpecificSteps,
  Localization.translator.triggerResourceClosureAfterSpecificWorkflowStep,
  Localization.translator.linkWorkflowToAnotherWorkflow,
  Localization.translator.createTemplatesFromImportedFormsToReuseAcrossProjects,
  Localization.translator
    .assignResponsibilityForWorkflowStepsToSpecificCompanies,
  Localization.translator.rollUpFormDataIntoExportableReports,
  Localization.translator.attachDocumentsAndPhotosToAnyWorkflow,
]

const humorMessages = [
  Localization.translator.safetyFirstNoHardHatRequiredWhileWeProcessYourForm,
  Localization.translator.ourScannersLiftFormsFasterThanALifterLiftsSteel,
  Localization.translator.everSeenPaperworkMoveThisFastOnAJobsiteNeitherHaveWe,
  Localization.translator.importingFormsNoRFIsRequired,
  Localization.translator.dontWorryThisWontTakeAsLongAsAConcretePour,
  Localization.translator
    .processingFormsFasterThanFindingTheRightWrenchInYourToolbox,
  Localization.translator.proTipThisIsTheOnlyTimePaperworkWillDoTheWorkForYou,
  Localization.translator.scanningWithoutThePaperCuts,
  Localization.translator.ourImportToolDoesntNeedAForemanYellingAtIt,
  Localization.translator.almostThereUnlikeThatElevatorInstallation,
]

const wrapUpMessages = [
  Localization.translator.wrappingThingsUp,
  Localization.translator.puttingFinishingTouchesOnYourForm,
  Localization.translator.justOneMoreCheck,
]

enum MessagePhase {
  Status = 'status',
  TipsAndHumor = 'tipsAndHumor',
  HumorAndWrapUp = 'humorAndWrapUp',
}

interface IProps {
  smartFormImportManagerStore?: SmartFormImportManagerStore
}

@inject('smartFormImportManagerStore')
@observer
export default class GlobalSmartFormImportProgress extends React.Component<IProps> {
  private messageInterval: NodeJS.Timeout = null
  private phaseTimeout1: NodeJS.Timeout = null
  private phaseTimeout2: NodeJS.Timeout = null
  private dialogRef = React.createRef<HTMLDivElement>()
  private dragStartX: number = 0
  private dragStartY: number = 0
  private dragStartLeft: number = 0
  private dragStartTop: number = 0

  @observable private previousIsImporting: boolean = false
  @observable private currentMessage: string = this.getStatusMessage()
  @observable private currentPhase: MessagePhase = MessagePhase.Status
  @observable private isFading: boolean = false
  @observable private posX: number = null
  @observable private posY: number = null
  @observable private isDragging: boolean = false

  public constructor(props: IProps) {
    super(props)
    this.previousIsImporting = props.smartFormImportManagerStore.isImporting

    makeObservable(this)
  }

  private startTimers(): void {
    runInAction(() => {
      this.currentPhase = MessagePhase.Status
      this.isFading = false
    })

    this.messageInterval = setInterval(() => {
      this.setNextMessage()
    }, 7000)

    this.phaseTimeout1 = setTimeout(() => {
      this.currentPhase = MessagePhase.TipsAndHumor
    }, 30000)

    this.phaseTimeout2 = setTimeout(() => {
      this.currentPhase = MessagePhase.HumorAndWrapUp
    }, 90000)
  }

  private setNextMessage() {
    runInAction(() => {
      this.isFading = true
    })

    setTimeout(() => {
      runInAction(() => {
        this.currentMessage = this.getMessageForCurrentPhase()
        this.isFading = false
      })
    }, 300)
  }

  private clearTimers(): void {
    if (this.messageInterval) {
      clearInterval(this.messageInterval)
      this.messageInterval = null
    }
    if (this.phaseTimeout1) {
      clearTimeout(this.phaseTimeout1)
      this.phaseTimeout1 = null
    }
    if (this.phaseTimeout2) {
      clearTimeout(this.phaseTimeout2)
      this.phaseTimeout2 = null
    }
  }

  public componentDidUpdate(): void {
    const currentIsImporting =
      this.props.smartFormImportManagerStore.isImporting

    if (!this.previousIsImporting && currentIsImporting) {
      this.clearTimers()
      this.startTimers()
      runInAction(() => {
        this.posX = null
        this.posY = null
      })
    }

    if (this.previousIsImporting && !currentIsImporting) {
      this.clearTimers()
    }

    runInAction(() => {
      this.previousIsImporting = currentIsImporting
    })
  }

  public componentWillUnmount() {
    this.clearTimers()
    document.removeEventListener('pointermove', this.handlePointerMove)
    document.removeEventListener('pointerup', this.handlePointerUp)
  }

  private handlePointerDown = (event: React.PointerEvent): void => {
    const dialog = this.dialogRef.current
    if (!dialog) return

    const rect = dialog.getBoundingClientRect()
    this.dragStartX = event.clientX
    this.dragStartY = event.clientY
    this.dragStartLeft = rect.left
    this.dragStartTop = rect.top

    runInAction(() => {
      this.isDragging = true
    })

    document.addEventListener('pointermove', this.handlePointerMove)
    document.addEventListener('pointerup', this.handlePointerUp)
  }

  private handlePointerMove = (event: PointerEvent): void => {
    const dx = event.clientX - this.dragStartX
    const dy = event.clientY - this.dragStartY

    runInAction(() => {
      this.posX = this.dragStartLeft + dx
      this.posY = this.dragStartTop + dy
    })
  }

  private handlePointerUp = (): void => {
    document.removeEventListener('pointermove', this.handlePointerMove)
    document.removeEventListener('pointerup', this.handlePointerUp)

    this.snapToClosestCorner()

    runInAction(() => {
      this.isDragging = false
    })
  }

  private snapToClosestCorner(): void {
    const dialog = this.dialogRef.current
    if (!dialog) return

    const rect = dialog.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const viewW = window.innerWidth
    const viewH = window.innerHeight

    const margin = 20
    const left = centerX < viewW / 2 ? margin : viewW - rect.width - margin
    const top = centerY < viewH / 2 ? margin : viewH - rect.height - margin

    runInAction(() => {
      this.posX = left
      this.posY = top
    })
  }

  private getStatusMessage(): string {
    return this.getRandomMessage(statusMessages)
  }

  private getTipsAndHumorMessage(): string {
    const combinedMessages = [...tipMessages, ...humorMessages]
    return this.getRandomMessage(combinedMessages)
  }

  private getHumorAndWrapUpMessage(): string {
    const combinedMessages = [...humorMessages, ...wrapUpMessages]
    return this.getRandomMessage(combinedMessages)
  }

  private getRandomMessage(messages: string[]): string {
    const randomIndex = Math.floor(Math.random() * messages.length)
    const message = messages[randomIndex]
    if (message === this.currentMessage) {
      return this.getRandomMessage(messages)
    }
    return message
  }

  private getMessageForCurrentPhase(): string {
    switch (this.currentPhase) {
      case MessagePhase.Status:
        return this.getStatusMessage()
      case MessagePhase.TipsAndHumor:
        return this.getTipsAndHumorMessage()
      case MessagePhase.HumorAndWrapUp:
        return this.getHumorAndWrapUpMessage()
    }
  }

  public render() {
    if (!this.props.smartFormImportManagerStore.isImporting) {
      return null
    }

    const positionStyle: React.CSSProperties =
      this.posX !== null
        ? {
            left: this.posX,
            top: this.posY,
            right: 'auto',
            bottom: 'auto',
            transition: this.isDragging
              ? 'none'
              : 'left 0.3s ease-out, top 0.3s ease-out',
          }
        : {}

    return (
      <div
        ref={this.dialogRef}
        className="smart-form-import-progress-dialog white-back"
        style={positionStyle}
      >
        <div className="tw:flex tw:flex-col no-outline-container tw:bg-background!">
          <div
            className="tw:flex tw:items-center tw:justify-between tw:mb-4 tw:border-0 tw:border-b tw:border-solid tw:border-border tw:p-2.5 tw:cursor-grab tw:select-none tw:active:cursor-grabbing"
            onPointerDown={this.handlePointerDown}
          >
            <h3 className="tw:m-0 tw:text-title-md tw:capitalize tw:text-center tw:flex-1 tw:pointer-events-none">
              {Localization.translator.smartFormImport}
            </h3>
          </div>
          <div className="pa10">
            <div className="progress-header">
              <span className="thinking-dot">{BULLET}</span>
              {Localization.translator.workingOnIt}
            </div>
            <div
              className={classList({
                'progress-message': true,
                fading: this.isFading,
              })}
            >
              {this.currentMessage}
            </div>
          </div>
        </div>
      </div>
    )
  }
}
